import React, { useEffect, useState } from 'react';
import {
  Typography,
  useTheme,
  Box,
  Card,
  CardMedia,
  Container,
  Tooltip,
  CardActionArea,
  LinearProgress,
  Chip,
} from '@mui/material';
import { Stack } from '@mui/system';
import { DetailedUserAchievement, Profile } from '@par/shared/types/player';
import { Achievement } from '../room/types';
import { fetchGet } from '../../api/common';
import { StyledPaperItem } from '../../common/mui-components';
import { ProfileAchievementsDetailsDialog } from '../../dialog/ProfileAchievementsDetailsDialog';
import { useParams } from 'react-router-dom';
import { fetchProfileById } from '@par/shared/components/profile';

export const ProfileAchievements = () => {
  const theme = useTheme();
  const { profileId } = useParams();
  const [viewProfile, setViewProfile] = useState<Profile>();

  const [allAchievements, setAllAchievements] = useState<Achievement[]>();
  const [detailedUserAchievements, setDetailedUserAchievements] =
    useState<DetailedUserAchievement[]>();
  const [openAchievementsDialog, setOpenAchievementsDialog] = useState(false);
  const [clickedAchievement, setClickedAchievement] = useState<Achievement>();

  useEffect(() => {
    fetchProfilePageData(profileId);
  }, []);

  const fetchProfilePageData = async (profileId: string) => {
    const profileRes: Profile = await fetchProfileById(profileId);
    setViewProfile(profileRes);
  };

  useEffect(() => {
    if (!viewProfile) return;
    fetchAllAchievements();
    fetchUserAchievements(viewProfile.id);
  }, [viewProfile]);

  const fetchAllAchievements = async () => {
    const res = await fetchGet('/achievements/all-achievements');
    setAllAchievements(res);
  };

  const fetchUserAchievements = async (profileId: string) => {
    const res = await fetchGet(`/achievements/user/${profileId}`);
    if (!res) return;
    setDetailedUserAchievements(res);
  };

  const userAchieved = (name: string) => {
    const achievement = detailedUserAchievements.find(
      (a) => a.achievement.name === name
    );
    if (!achievement) return false;
    return true;
  };

  const achievementCount = (name: string) => {
    const achievement = detailedUserAchievements.find(
      (a) => a.achievement.name === name
    );
    if (!achievement) return 0;
    if (!achievement.achievement.counter) return 0;
    return achievement.count;
  };

  const achievementProgressCount = (name: string) => {
    const achievement = detailedUserAchievements.find(
      (a) => a.achievement.name === name
    );
    if (!achievement) return 0;
    return achievement.progressCount;
  };

  const getProgressBarValue = (a: Achievement) => {
    if (!detailedUserAchievements) return 0;
    const foundAchievement = detailedUserAchievements.find(
      (ua) => ua.achievement.name === a.name
    );
    if (!foundAchievement) return 0;
    if (!foundAchievement.progressCount) return 0;
    return (foundAchievement.progressCount / 1000) * 100;
  };

  const handleAchievementClick = (a: Achievement) => {
    setClickedAchievement(a);
    setOpenAchievementsDialog(true);
  };

  const achievementBox = (a: Achievement) => {
    return (
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Tooltip title={a.displayName} placement="top">
          <Stack
            sx={{
              filter: userAchieved(a.name) ? '' : 'grayscale(100%)',
              opacity: userAchieved(a.name) ? 1 : 0.5,
              display: 'flex',
              marginLeft: 1,
              marginRight: 1,
              marginBottom: 3,
              width: '150px',
            }}
          >
            <StyledPaperItem
              elevation={3}
              sx={{
                boxShadow: userAchieved(a.name)
                  ? `
                  inset 0 0 10px #e0e0e0,
                  inset 20px 0 80px #f7ecb2,
                  inset -20px 0 80px #caf3f3,
                  inset 20px 0 300px #e0b8e0,
                  inset -20px 0 300px #f6ef61,
                  0 0 10px #e0e0e0,
                  -5px 0 20px #c0d1bc,
                  5px 0 20px #fffd5f;`
                  : 'none',
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  // borderRadius: 2,
                  position: 'relative',
                }}
              >
                <CardActionArea onClick={() => handleAchievementClick(a)}>
                  {achievementProgressCount(a.name) ? (
                    <Chip
                      label={`${achievementProgressCount(a.name)} / 1000`}
                      variant="filled"
                      sx={{
                        position: 'absolute',
                        top: '15%',
                        left: '50%',
                        transform: 'translate(-50%,-50%)',
                        color: 'black',
                        fontWeight: 600,
                        backgroundColor: theme.palette.success.main,
                        border: '1px solid',
                        borderColor: theme.palette.success.main,
                      }}
                    />
                  ) : achievementCount(a.name) ? (
                    <Chip
                      label={achievementCount(a.name)}
                      variant="filled"
                      sx={{
                        position: 'absolute',
                        top: '2%',
                        right: '2%',
                        color: 'black',
                        fontWeight: 600,
                        backgroundColor: theme.palette.secondary.light,
                        border: '1px solid',
                        borderColor: theme.palette.secondary.light,
                      }}
                    />
                  ) : null}
                  <Stack>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CardMedia
                        component="img"
                        // image={resizeImage(a.imagePublicId, 50, 50)}
                        image={a.imageUrl}
                        alt={a.displayName}
                        sx={{
                          objectFit: 'scale-down',
                          height: '100px',
                          width: '150px',
                        }}
                      />
                    </Box>
                    <Typography
                      textAlign={'center'}
                      sx={{
                        color: userAchieved(a.name)
                          ? theme.palette.dailies.main
                          : 'gray',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'pre',
                        padding: 1,
                        fontWeight: 600,
                      }}
                    >
                      {a.displayName}
                    </Typography>
                    {a.progressBar ? (
                      <LinearProgress
                        variant="determinate"
                        value={getProgressBarValue(a)}
                        color="success"
                      />
                    ) : null}
                  </Stack>
                </CardActionArea>
              </Card>
            </StyledPaperItem>
          </Stack>
        </Tooltip>
      </Box>
    );
  };

  return (
    <>
      {clickedAchievement ? (
        <ProfileAchievementsDetailsDialog
          achievement={clickedAchievement}
          close={() => setOpenAchievementsDialog(false)}
          open={openAchievementsDialog}
          viewProfile={viewProfile}
        />
      ) : null}
      <Container sx={{ paddingBottom: '100px' }}>
        <Stack spacing={5}>
          <Typography variant="h4" textAlign={'center'} letterSpacing={20}>
            <Typography
              variant="h6"
              textAlign={'center'}
              letterSpacing={5}
              mb={1}
            >
              {viewProfile?.username}'s
            </Typography>
            ACHIEVEMENTS
          </Typography>
          {detailedUserAchievements?.filter(
            (a) => a.achievement?.category === 'Trophy'
          ).length ? (
            <>
              <Typography
                variant="h5"
                textAlign={'center'}
                letterSpacing={5}
                fontWeight={600}
                sx={{
                  fontFamily: 'blacksword',
                }}
              >
                Trophies
              </Typography>
              <Stack
                direction="row"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {detailedUserAchievements
                  ? detailedUserAchievements
                      .map((a) => ({ achievements: a.achievement }))
                      .filter((a) => a.achievements?.category === 'Trophy')
                      .map((a) => achievementBox(a.achievements))
                  : null}
              </Stack>
            </>
          ) : null}
          <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={5}
            fontWeight={600}
            sx={{ color: '#cda68c' }}
          >
            Exploration
          </Typography>
          <Stack
            direction="row"
            sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}
          >
            {detailedUserAchievements && allAchievements
              ? allAchievements
                  .filter((a) => a.category === 'Exploration' && !a.disabled)
                  .sort((a, b) => a.order - b.order)
                  .map((a) => achievementBox(a))
              : null}
          </Stack>
          <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={5}
            fontWeight={600}
            sx={{ color: theme.palette.dailies.main }}
          >
            Daily
          </Typography>
          <Stack
            direction="row"
            sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}
          >
            {detailedUserAchievements && allAchievements
              ? allAchievements
                  .filter((a) => a.category === 'Daily' && !a.disabled)
                  .sort((a, b) => a.order - b.order)
                  .map((a) => achievementBox(a))
              : null}
          </Stack>
          <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={5}
            fontWeight={600}
            sx={{ color: '#B0E0E6' }}
          >
            Friendly
          </Typography>
          <Stack
            direction="row"
            sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}
          >
            {detailedUserAchievements && allAchievements
              ? allAchievements
                  .filter((a) => a.category === 'Friendly' && !a.disabled)
                  .sort((a, b) => a.order - b.order)
                  .map((a) => achievementBox(a))
              : null}
          </Stack>
          <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={5}
            fontWeight={600}
            sx={{ color: '#FF1493' }}
          >
            Solo
          </Typography>
          <Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Solo')
                    .filter((a) => a.group === 1)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Solo' && !a.disabled)
                    .filter((a) => a.group === 2)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Solo' && !a.disabled)
                    .filter((a) => a.group === 3)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
          </Stack>
          <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={5}
            fontWeight={600}
            sx={{ color: '#ff8c00' }}
          >
            Sit & Go
          </Typography>
          <Stack
            direction="row"
            sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}
          >
            {detailedUserAchievements && allAchievements
              ? allAchievements
                  .filter((a) => a.category === 'Sit n Go' && !a.disabled)
                  .sort((a, b) => a.order - b.order)
                  .map((a) => achievementBox(a))
              : null}
          </Stack>
          <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={5}
            fontWeight={600}
            sx={{ color: theme.palette.ranked.main }}
          >
            Ranked
          </Typography>
          <Stack
            direction="row"
            sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}
          >
            {detailedUserAchievements && allAchievements
              ? allAchievements
                  .filter((a) => a.category === 'Ranked' && !a.disabled)
                  .sort((a, b) => a.order - b.order)
                  .map((a) => achievementBox(a))
              : null}
          </Stack>
          <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={5}
            fontWeight={600}
            sx={{ color: '#E6DF93' }}
          >
            Ghost
          </Typography>
          <Stack
            direction="row"
            sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}
          >
            {detailedUserAchievements && allAchievements
              ? allAchievements
                  .filter((a) => a.category === 'Ghost')
                  .sort((a, b) => a.order - b.order)
                  .map((a) => achievementBox(a))
              : null}
          </Stack>
          <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={5}
            fontWeight={600}
            sx={{ color: theme.palette.survival.main }}
          >
            Survival
          </Typography>
          <Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Survival' && !a.disabled)
                    .filter((a) => a.group === 1)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Survival' && !a.disabled)
                    .filter((a) => a.group === 2)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Survival' && !a.disabled)
                    .filter((a) => a.group === 3)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
          </Stack>
          <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={5}
            fontWeight={600}
            sx={{ color: theme.palette.demon.main }}
          >
            Demon
          </Typography>
          <Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Demon' && !a.disabled)
                    .filter((a) => a.group === 1)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Demon' && !a.disabled)
                    .filter((a) => a.group === 2)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Demon' && !a.disabled)
                    .filter((a) => a.group === 3)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
          </Stack>
          <Box width="100%" display="flex" justifyContent={'center'}>
            <Typography
              variant="h5"
              textAlign={'center'}
              letterSpacing={5}
              fontWeight={600}
              sx={{
                width: 'fit-content',
                backgroundImage:
                  'linear-gradient(to left, violet, indigo, blue, green, yellow, orange, red)',
                color: 'transparent',
                WebkitBackgroundClip: 'text',
              }}
            >
              Starter Kit
            </Typography>
          </Box>
          <Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Starter Kit' && !a.disabled)
                    .filter((a) => a.group === 1)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Starter Kit' && !a.disabled)
                    .filter((a) => a.group === 2)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Starter Kit' && !a.disabled)
                    .filter((a) => a.group === 3)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
          </Stack>
          {/* <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={10}
            sx={{ color: '#00bfff' }}
          >
            League
          </Typography>
          <Stack
            direction="row"
            sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}
          >
            {detailedUserAchievements && allAchievements
              ? allAchievements
                  .filter((a) => a.category === 'League')
                  .sort((a, b) => a.order - b.order)
                  .map((a) => achievementBox(a))
              : null}
          </Stack> */}
          <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={5}
            fontWeight={600}
            sx={{ color: '#e7ba54' }}
          >
            Trinkets
          </Typography>
          <Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Trinkets' && !a.disabled)
                    .filter((a) => a.group === 1)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Trinkets' && !a.disabled)
                    .filter((a) => a.group === 2)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Trinkets' && !a.disabled)
                    .filter((a) => a.group === 3)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Trinkets' && !a.disabled)
                    .filter((a) => a.group === 4)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              {detailedUserAchievements && allAchievements
                ? allAchievements
                    .filter((a) => a.category === 'Trinkets' && !a.disabled)
                    .filter((a) => a.group === 5)
                    .sort((a, b) => a.order - b.order)
                    .map((a) => achievementBox(a))
                : null}
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </>
  );
};
